import { SETUP_STATUS_META } from './setupStatus';
import { RunButtons } from '@/features/runs/RunButtons';
import { useState } from 'react';
import { Check, Copy, ExternalLink, FolderOpen, Loader2 } from 'lucide-react';
import { DisclosureToggle, NoteDisclosure } from '@/components/Disclosure';
import { Hint } from '@/components/Hint';
import { Can } from '@/auth/guards';
import { Button } from '@/components/ui/button';
import { StatusPill } from '@/components/Status';
import { statusInk, type StatusTone } from '@/lib/status';
import { cn } from '@/lib/utils';
import {
  getSetupFolder,
  type SetupFolderStaging,
  type SetupFolderSummary,
} from '@/api/orderPrep';
import { fmtDate } from './lib';

// ---------------------------------------------------------------------------
// The setup workspace folder inside the Set up view: a summary chip on the
// journey row and an expandable panel with the folder's state — what's built,
// what it waits on, embargo windows — plus the deliverable itself: the
// validated products.json, copyable straight from here so submission doesn't
// depend on the machine the run happened on.
// ---------------------------------------------------------------------------



/** Always the object form: legacy string values were normalized by
 *  20260820_setup_staging_normalize.sql, and the sync path canonicalises new
 *  ones at ingestion. */
function stagingSummary(staging: SetupFolderStaging | null | undefined): string | null {
  if (staging == null) return null;
  if (staging.expired) {
    return `staging expired${staging.expired_at_utc ? ` ${fmtDate(staging.expired_at_utc)}` : ''} — re-stage before submitting`;
  }
  if (staging.staged) {
    return `staged ${staging.last_staged_at_utc ? fmtDate(staging.last_staged_at_utc) : ''}${staging.images ? ` (${staging.images} images)` : ''}`;
  }
  return null;
}

/**
 * The row chip: one glance = does a folder exist, is it ready, how many built.
 * Clicking toggles the expanded panel.
 */
export function SetupFolderChip({ folder, expanded, onToggle }: {
  folder: SetupFolderSummary;
  expanded: boolean;
  onToggle: () => void;
}) {
  const meta = SETUP_STATUS_META[folder.status ?? ''] ?? {
    label: folder.status ?? 'unknown',
    tone: 'neutral' as const,
    hint: 'Unrecognized folder status',
  };
  // A status pill inside one disclosure toggle: the pill says what state the
  // folder is in, the chevron is the only thing that opens it.
  return (
    <Hint content={`${meta.hint}${folder.syncedAt ? ` Synced ${fmtDate(folder.syncedAt)}.` : ''}`}>
      <span className="inline-flex">
        <DisclosureToggle open={expanded} onToggle={onToggle}>
          <FolderOpen className="h-3 w-3" />
          <StatusPill tone={meta.tone}>{meta.label}</StatusPill>
          {folder.products != null && folder.products > 0 && (
            <span className="tabular-nums">{folder.products} built</span>
          )}
          {folder.lastValidated && (
            <span className="tabular-nums">validated {fmtDate(folder.lastValidated)}</span>
          )}
          {folder.submittedAt && (
            <span className={cn('tabular-nums', statusInk('success'))}>submitted {fmtDate(folder.submittedAt)}</span>
          )}
        </DisclosureToggle>
      </span>
    </Hint>
  );
}

/** One fact line in the expanded panel. Renders nothing without a value. */
function FactRow({ label, children, tone }: {
  label: string;
  children: React.ReactNode;
  /** Only when the fact is a state; a plain fact stays in body ink. */
  tone?: StatusTone;
}) {
  if (children == null || children === '') return null;
  return (
    <div className="flex gap-2 text-xs">
      <span className="w-28 shrink-0 text-muted-foreground">{label}</span>
      <span className={cn('min-w-0', tone && statusInk(tone))}>{children}</span>
    </div>
  );
}

function CopyPayloadButton({ folder }: { folder: SetupFolderSummary }) {
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!folder.hasPayload) {
    return (
      <Hint content="The payload has not been synced yet.">
        <span className="text-xs text-muted-foreground">payload not synced yet</span>
      </Hint>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5">
      {/* GET /order-prep/setup-state/:folder is orders:read:costs — the
          payload prices every product — so the copy exists only with it. */}
      <Can code="orders:read:costs">
      <Hint content="Copies the validated products.json for the importer.">
        <Button
          size="sm"
          variant="outline"
          className="h-6 gap-1 px-2 text-xs"
          disabled={busy}
          onClick={async () => {
            setBusy(true);
            setError(null);
            try {
              const detail = await getSetupFolder(folder.folder);
              if (!detail?.payload) throw new Error('Payload missing on the server');
              await navigator.clipboard.writeText(JSON.stringify(detail.payload, null, 2));
              setCopied(true);
              setTimeout(() => setCopied(false), 2500);
            } catch (e) {
              setError(e instanceof Error ? e.message : 'Copy failed');
            } finally {
              setBusy(false);
            }
          }}
        >
          {busy ? <Loader2 className="h-3 w-3 animate-spin" />
            : copied ? <Check className={cn('h-3 w-3', statusInk('success'))} />
            : <Copy className="h-3 w-3" />}
          {copied ? 'Copied' : 'Copy products.json'}
        </Button>
      </Hint>
      </Can>
      {folder.payloadProducts != null && (
        <span className="text-xs text-muted-foreground tabular-nums">
          {folder.payloadProducts} products
          {folder.payloadUpdatedAt ? ` · ${fmtDate(folder.payloadUpdatedAt)}` : ''}
        </span>
      )}
      {error && <span className={cn('text-xs', statusInk('danger'))}>{error}</span>}
    </span>
  );
}

/** Review + submit happens on the inventory app's Setup Preview page. */
const SETUP_PREVIEW_URL = 'https://tools.acherryontop.com/setup-preview';

function SubmittedFact({ folder }: { folder: SetupFolderSummary }) {
  const last = folder.lastSubmission;
  if (!folder.submittedAt) return null;
  const changedSince = folder.payloadUpdatedAt && new Date(folder.payloadUpdatedAt) > new Date(folder.submittedAt);
  return (
    <FactRow label="Submitted" tone="success">
      <>
        {fmtDate(folder.submittedAt)}
        {folder.submittedBy ? ` by ${folder.submittedBy}` : ''}
        {last ? ` — ${last.created_count} created${last.errored_count ? `, ${last.errored_count} errored` : ''}` : ''}
        {folder.submissionCount && folder.submissionCount > 1 ? ` (${folder.submissionCount} submits)` : ''}
        {last?.product_tool_url && (
          <>
            {' · '}
            <a className="underline" href={last.product_tool_url} target="_blank" rel="noreferrer">Product Tool</a>
          </>
        )}
        {changedSince && <span className={statusInk('warning')}> · payload changed since</span>}
      </>
    </FactRow>
  );
}

/**
 * The expanded panel: everything the setup state holds, promoted or not, plus the
 * copyable payload. Read-only by design — the workspace on disk is still the
 * writer; this panel is the decoupled window onto it.
 */
export function SetupFolderPanel({ folder }: { folder: SetupFolderSummary }) {
  const s = folder.state ?? {};
  const staging = stagingSummary(s.staging);
  const blockedOn = Array.isArray(s.blocked_on) ? s.blocked_on.filter(Boolean) : [];
  const stagingNote = s.staging?.note ? String(s.staging.note) : null;
  return (
    <div className="mt-1.5 space-y-1 rounded-md border border-border/40 bg-muted/20 px-3 py-2">
      <div className="flex items-center gap-2 text-xs">
        <span className="font-mono text-muted-foreground">{folder.folder}</span>
        <span className="ml-auto" />
        {folder.status !== 'parked' && folder.status !== 'archived' && <RunButtons subject={{ setupFolder: folder.folder }} />}
        {folder.hasPayload && (
          <Hint content="Review every product and submit to production (inventory app).">
            <Button asChild size="sm" variant="outline" className="h-6 gap-1 px-2 text-xs">
              <a href={`${SETUP_PREVIEW_URL}?folder=${encodeURIComponent(folder.folder)}`} target="_blank" rel="noreferrer">
                <ExternalLink className="h-3 w-3" />
                Preview &amp; submit
              </a>
            </Button>
          </Hint>
        )}
        <CopyPayloadButton folder={folder} />
      </div>
      <SubmittedFact folder={folder} />
      <FactRow label="Waiting on">{s.waiting_on ? String(s.waiting_on) : null}</FactRow>
      {blockedOn.length > 0 && (
        <FactRow label="Blocked on" tone="danger">
          {blockedOn.map(String).join(' · ')}
        </FactRow>
      )}
      <FactRow label="Staging" tone={staging?.includes('expired') ? 'warning' : undefined}>
        {staging}
      </FactRow>
      {stagingNote && <FactRow label="">{stagingNote}</FactRow>}
      <FactRow label="Embargo">
        {folder.embargo ? `don't list before ${fmtDate(folder.embargo)}` : null}
      </FactRow>
      <FactRow label="Submit window">
        {folder.doNotSubmitBefore ? `don't submit before ${fmtDate(folder.doNotSubmitBefore)}` : null}
      </FactRow>
      {s.notes && <NoteDisclosure text={String(s.notes)} />}
      <FactRow label="Synced">
        {folder.syncedAt ? fmtDate(folder.syncedAt) : 'never'}
        {folder.lastValidated ? ` · validated ${fmtDate(folder.lastValidated)}` : ''}
      </FactRow>
    </div>
  );
}
